import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, AlertTriangle, CheckCircle2, Clock, ListChecks, Link2, Plus, Shield, Award, ClipboardCheck, Briefcase, Target, TrendingUp } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { hasPlan } from "@/lib/plans";
import UpgradeLock from "@/components/UpgradeLock";
import UsageMeter from "@/components/UsageMeter";

// Map the new "Pursue / Pursue with Caution / Do Not Pursue" recommendations and the legacy
// "Bid / Bid with Caution / Avoid" values onto the same emerald/amber/red palette.
const _finalRec = (r) => r?.final_recommendation
  || (r?.recommendation === "Bid" ? "Pursue"
    : r?.recommendation === "Avoid" ? "Do Not Pursue"
    : r?.recommendation === "Bid with Caution" ? "Pursue with Caution"
    : r?.recommendation || "Pursue with Caution");
const recColor = (label) => {
  const v = typeof label === "string" ? label : _finalRec(label);
  if (v === "Pursue" || v === "Bid") return "text-emerald-700 bg-emerald-50 border-emerald-200";
  if (v === "Do Not Pursue" || v === "Avoid") return "text-red-700 bg-red-50 border-red-200";
  return "text-amber-700 bg-amber-50 border-amber-200";
};
const riskColor = (lvl) => lvl === "High" ? "text-red-700 bg-red-50 border-red-200"
  : lvl === "Low" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
  : "text-amber-700 bg-amber-50 border-amber-200";
const scoreColor = (n) => n >= 70 ? "text-emerald-600" : n >= 40 ? "text-amber-600" : "text-red-600";
const barColor = (n) => n >= 70 ? "bg-emerald-500" : n >= 40 ? "bg-amber-500" : "bg-red-500";

export default function Triage() {
  const { user } = useAuth();
  const locked = !hasPlan(user?.plan, "growth");
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [resultTitle, setResultTitle] = useState("");
  const [adding, setAdding] = useState(false);
  const [history, setHistory] = useState([]);
  const [usageRefresh, setUsageRefresh] = useState(0);

  const loadHistory = useCallback(() => api.get("/bid-triage/history").then((r) => setHistory(r.data.reports)), []);
  useEffect(() => { if (!locked) loadHistory(); }, [locked, loadHistory]);

  if (locked) return <UpgradeLock required="growth" feature="AI Bid Package Analyzer" description="Upgrade to Growth to upload any RFP / RFQ / IFB / bid package and get a full pre-bid intake report: executive summary, scope, deadlines, insurance / bonding / certification breakdowns, submission & compliance checklists, risk assessment, and a Pursue / Pursue-with-Caution / Do-Not-Pursue recommendation." />;

  const submit = async () => {
    if (!file && !text.trim() && !url.trim()) { toast.error("Upload a file, paste text, or add a link"); return; }
    setLoading(true); setResult(null);
    try {
      const form = new FormData();
      if (file) form.append("file", file);
      else if (url.trim()) form.append("url", url.trim());
      else if (text.trim()) form.append("text", text);
      const r = await api.post("/bid-triage", form, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data.result);
      setResultTitle(r.data.title || "External bid");
      loadHistory();
      toast.success("Bid scored");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Analysis failed"));
    } finally {
      setLoading(false);
      setUsageRefresh((n) => n + 1);
    }
  };

  const addToCrm = async () => {
    if (!hasPlan(user?.plan, "pro")) { toast.error("Bid CRM is a Pro feature — upgrade to track bids in your pipeline."); return; }
    setAdding(true);
    try {
      await api.post("/bids", {
        title: resultTitle || "External bid",
        agency: result.agency || "",
        amount: result.estimated_value || 0,
        stage: "triage",
        qualification_score: result.qualification_score ?? null,
        win_probability: result.win_probability ?? null,
        notes: result.summary || "",
      });
      toast.success("Added to Bid CRM");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add to CRM");
    } finally { setAdding(false); }
  };

  return (
    <div className="space-y-6" data-testid="triage-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Bid Package Analyzer</h1>
        <p className="text-slate-500 mt-1">Upload an RFP / RFQ / IFB or PDF bid package. AI returns an executive summary, scope, deadlines, insurance / bonding / certification breakdowns, submission &amp; compliance checklists, risk assessment, and a Pursue / Pursue-with-Caution / Do-Not-Pursue call.</p>
      </div>

      <UsageMeter refresh={usageRefresh} className="max-w-sm" />

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-4">
          <label className="block border-2 border-dashed border-slate-200 rounded-xl p-8 text-center cursor-pointer hover:border-accent hover:bg-slate-50 transition-colors" data-testid="file-dropzone">
            <input type="file" accept=".pdf,.txt" className="hidden" data-testid="file-input" onChange={(e) => { setFile(e.target.files[0]); setUrl(""); }} />
            <Upload className="h-8 w-8 mx-auto text-slate-400" />
            <p className="mt-3 text-sm font-medium text-slate-700">{file ? file.name : "Drop a PDF or TXT, or click to browse"}</p>
            <p className="text-xs text-slate-400 mt-1">RFP, RFQ, IFB, plans, bid packages</p>
          </label>

          <div className="relative">
            <div className="text-center text-xs text-slate-400 uppercase tracking-wider my-2">or paste a link</div>
            <div className="relative">
              <Link2 className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={url} onChange={(e) => { setUrl(e.target.value); setFile(null); }} data-testid="rfp-url-input" placeholder="https://sam.gov/opp/… or any public bid page / PDF" className="w-full rounded-md border border-slate-200 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent" />
            </div>
          </div>

          <div className="relative">
            <div className="text-center text-xs text-slate-400 uppercase tracking-wider my-2">or paste text</div>
            <textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="rfp-text-input" rows={5} placeholder="Paste solicitation text here…" className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent" />
          </div>

          <button onClick={submit} disabled={loading} data-testid="run-triage-button" className="w-full bg-emerald-900 text-white rounded-md py-3 text-sm font-medium hover:bg-emerald-800 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
            <FileText className="h-4 w-4" /> {loading ? "Scoring…" : "Score This Bid"}
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 min-h-[300px]">
          {loading ? (
            <div className="h-full flex flex-col items-center justify-center gap-3 py-16">
              <div className="h-8 w-8 border-[3px] border-slate-200 border-t-slate-900 rounded-full animate-spin" />
              <p className="text-sm text-slate-500 font-data">Scoring fit & extracting requirements…</p>
            </div>
          ) : result ? (
            <div className="space-y-5" data-testid="triage-result">
              <div className="grid grid-cols-2 gap-3" data-testid="triage-scores">
                <ScoreCard label="Qualification" value={result.qualification_score} />
                <ScoreCard label="Win Probability" value={result.win_probability} />
              </div>

              <div className={`rounded-xl border p-4 ${recColor(result)}`} data-testid="triage-recommendation">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wider">Final recommendation</p>
                  <span className="font-data text-sm">{result.confidence}% confidence</span>
                </div>
                <p className="font-heading text-2xl font-bold mt-1">{_finalRec(result)}</p>
                <p className="text-sm mt-2 text-slate-700">{result.rationale}</p>
              </div>

              <button onClick={addToCrm} disabled={adding} data-testid="add-to-crm-button" className="w-full bg-white border border-emerald-900 text-emerald-900 rounded-md py-2.5 text-sm font-medium hover:bg-emerald-50 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
                <Plus className="h-4 w-4" /> {adding ? "Adding…" : "Add to Bid CRM"}
              </button>

              {(result.executive_summary || result.summary) && (
                <Section icon={FileText} title="Executive Summary" testid="section-exec">
                  <p className="text-sm text-slate-700 leading-relaxed">{result.executive_summary || result.summary}</p>
                </Section>
              )}

              {result.scope_summary && (
                <Section icon={Briefcase} title="Scope of Work" testid="section-scope">
                  <p className="text-sm text-slate-700 leading-relaxed">{result.scope_summary}</p>
                </Section>
              )}

              <ResultList icon={Clock} title="Important Deadlines" items={result.deadlines} testid="section-deadlines" />
              <ResultList icon={Shield} title="Insurance Requirements" items={result.insurance_requirements} color="text-sky-600" testid="section-insurance" />
              <ResultList icon={Target} title="Bonding Requirements" items={result.bonding_requirements} color="text-indigo-600" testid="section-bonding" />
              <ResultList icon={Award} title="Certification Requirements" items={result.certification_requirements} color="text-emerald-700" testid="section-certifications" />
              <ResultList icon={ClipboardCheck} title="Submission Checklist" items={result.submission_checklist} color="text-emerald-800" testid="section-submission" />
              <ResultList icon={CheckCircle2} title="Compliance Checklist" items={result.compliance_checklist || result.compliance_items} color="text-emerald-700" testid="section-compliance" />

              {(result.risk_assessment?.factors?.length || result.risks?.length) ? (
                <Section icon={AlertTriangle} title="Risk Assessment" color="text-amber-600" testid="section-risks">
                  {result.risk_assessment?.overall_risk_level && (
                    <div className={`inline-flex items-center text-xs font-semibold rounded-full border px-2.5 py-0.5 mb-3 ${riskColor(result.risk_assessment.overall_risk_level)}`}>
                      {result.risk_assessment.overall_risk_level} overall risk
                    </div>
                  )}
                  <ul className="space-y-1 pl-1">
                    {(result.risk_assessment?.factors || result.risks || []).map((it, i) => (
                      <li key={`risk-${i}`} className="text-sm text-slate-700">• {it}</li>
                    ))}
                  </ul>
                </Section>
              ) : null}

              {(!result.executive_summary && !result.scope_summary && result.requirements?.length > 0) && (
                <ResultList icon={ListChecks} title="Key Requirements" items={result.requirements} testid="section-requirements-legacy" />
              )}
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center py-16 text-slate-400">
              <FileText className="h-10 w-10 mb-3" />
              <p className="text-sm">Your AI scoring &amp; analysis will appear here.</p>
            </div>
          )}
        </div>
      </div>

      {history.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
          <h3 className="font-heading text-lg font-semibold mb-4">Recent analyses</h3>
          <div className="space-y-2">
            {history.slice(0, 8).map((h) => (
              <button key={h.id} onClick={() => { setResult(h.result); setResultTitle(h.title); }} data-testid={`history-item-${h.id}`} className="w-full flex items-center justify-between text-left p-3 rounded-lg hover:bg-slate-50 transition-colors">
                <span className="text-sm text-slate-700 truncate">{h.title}</span>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${recColor(h.result)}`}>{_finalRec(h.result)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const ScoreCard = ({ label, value }) => {
  const v = Number.isFinite(value) ? value : 0;
  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</p>
      <p className={`font-heading text-3xl font-bold mt-1 ${scoreColor(v)}`}>{v}<span className="text-base text-slate-300">/100</span></p>
      <div className="mt-2 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${barColor(v)}`} style={{ width: `${v}%` }} />
      </div>
    </div>
  );
};

const ResultList = ({ icon: Icon, title, items, color = "text-slate-600", testid }) => {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid={testid}>
      <p className="text-sm font-semibold text-slate-900 mb-2 inline-flex items-center gap-2"><Icon className={`h-4 w-4 ${color}`} />{title}</p>
      <ul className="space-y-1 pl-1">
        {items.map((it, i) => <li key={`${title}-${i}`} className="text-sm text-slate-700">• {typeof it === "string" ? it : JSON.stringify(it)}</li>)}
      </ul>
    </div>
  );
};

const Section = ({ icon: Icon, title, color = "text-slate-600", testid, children }) => (
  <div data-testid={testid}>
    <p className="text-sm font-semibold text-slate-900 mb-2 inline-flex items-center gap-2"><Icon className={`h-4 w-4 ${color}`} />{title}</p>
    {children}
  </div>
);
